<script setup lang="ts">
import { ref, watch } from 'vue';
import { ChevronDown, ChevronUp } from 'lucide-vue-next';
import Button from '../button/Button.vue';

interface Props {
  modelValue?: string;
  disabled?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'change', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '00:00',
  disabled: false
});

const emit = defineEmits<Emits>();

// Parse initial hours and minutes from modelValue
const parseInitialTime = () => {
  const [hoursStr, minutesStr] = props.modelValue.split(':');
  return {
    hours: parseInt(hoursStr) || 0,
    minutes: parseInt(minutesStr) || 0
  };
};

const hours = ref(parseInitialTime().hours);
const minutes = ref(parseInitialTime().minutes);

// Increment/decrement functions with limits
const incrementHours = () => {
  if (props.disabled) return;
  hours.value = (hours.value + 1) % 24;
  emitTimeChange();
};

const decrementHours = () => {
  if (props.disabled) return;
  hours.value = (hours.value - 1 + 24) % 24;
  emitTimeChange();
};

const incrementMinutes = () => {
  if (props.disabled) return;
  minutes.value = (minutes.value + 1) % 60;
  emitTimeChange();
};

const decrementMinutes = () => {
  if (props.disabled) return;
  minutes.value = (minutes.value - 1 + 60) % 60;
  emitTimeChange();
};

// Format time values with leading zeros
const formattedHours = () => hours.value.toString().padStart(2, '0');
const formattedMinutes = () => minutes.value.toString().padStart(2, '0');

// Emit time change events
const emitTimeChange = () => {
  const timeString = `${formattedHours()}:${formattedMinutes()}`;
  emit('update:modelValue', timeString);
  emit('change', timeString);
};

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  const { hours: newHours, minutes: newMinutes } = parseInitialTime();
  hours.value = newHours;
  minutes.value = newMinutes;
});
</script>

<template>
  <div class="flex items-center gap-4 text-white">
    <!-- Hours -->
    <div class="flex flex-col items-center">
      <Button 
        variant="outline" 
        size="icon" 
        class="h-8 w-8 rounded-md" 
        @click="incrementHours"
        :disabled="disabled"
      >
        <ChevronUp class="h-4 w-4" />
      </Button>
      
      <span class="text-center py-1 min-w-[2ch] text-md">
        {{ formattedHours() }}
      </span>
      
      <Button 
        variant="outline" 
        size="icon" 
        class="h-8 w-8 rounded-md" 
        @click="decrementHours"
        :disabled="disabled"
      >
        <ChevronDown class="h-4 w-4" />
      </Button>
    </div>
    
    <!-- Separator -->
    <div class="text-md font-medium">:</div>
    
    <!-- Minutes -->
    <div class="flex flex-col items-center">
      <Button 
        variant="outline" 
        size="icon" 
        class="h-8 w-8 rounded-md" 
        @click="incrementMinutes"
        :disabled="disabled"
      >
        <ChevronUp class="h-4 w-4" />
      </Button>
      
      <span class="text-center py-1 min-w-[2ch] text-md">
        {{ formattedMinutes() }}
      </span>
      
      <Button 
        variant="outline" 
        size="icon" 
        class="h-8 w-8 rounded-md" 
        @click="decrementMinutes"
        :disabled="disabled"
      >
        <ChevronDown class="h-4 w-4" />
      </Button>
    </div>
  </div>
</template>